import {useDebounce} from '@github-ui/use-debounce'
import {SearchIcon, XCircleFillIcon} from '@primer/octicons-react'
import {FormControl, IconButton, TextInput} from '@primer/react'
import {useState, type Ref} from 'react'

import styles from './SearchInput.module.css'

export interface SearchInputProps {
  onSearch: (query: string) => void
  placeholder?: string
  'aria-label'?: string
  disabled?: boolean
  /**
   * Delay in milliseconds before triggering search. Defaults to 200ms.
   */
  debounceDelay?: number
  /** Lets callers move focus to the field, e.g. after dismissing a banner above it. */
  ref?: Ref<HTMLInputElement>
}

export function SearchInput({
  onSearch,
  placeholder = 'Search...',
  'aria-label': ariaLabel = 'Search',
  disabled = false,
  debounceDelay = 200,
  ref,
}: SearchInputProps) {
  const [searchQuery, setSearchQuery] = useState('')

  // Debounce the search to avoid excessive filtering on every keystroke
  const debouncedSearch = useDebounce(onSearch, debounceDelay)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.currentTarget.value
    setSearchQuery(newQuery)

    // For empty queries, search immediately to clear results
    if (newQuery.trim() === '') {
      onSearch('')
    } else {
      debouncedSearch(newQuery)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    onSearch('')
  }

  return (
    <div className={styles.searchContainer}>
      <FormControl className={styles.searchInputWrapper} disabled={disabled}>
        <FormControl.Label visuallyHidden>{ariaLabel}</FormControl.Label>
        <TextInput
          ref={ref}
          className={styles.searchInput}
          placeholder={placeholder}
          value={searchQuery}
          onChange={handleChange}
          leadingVisual={SearchIcon}
          trailingAction={
            searchQuery !== '' ? (
              <IconButton
                icon={XCircleFillIcon}
                onClick={clearSearch}
                aria-label="Clear search"
                variant="invisible"
                size="small"
                disabled={disabled}
              />
            ) : undefined
          }
        />
      </FormControl>
    </div>
  )
}
